import axios from 'axios';

const { REACT_APP_API_URL } = process.env;
const API_BASE_PATH = '/api/v1.0.0';

type AllowedEndpoints =
  | 'sessions'
  | 'currentSession'
  | 'users'
  | 'currentUser'
  | 'questions'
  | 'questionsList'
  | 'answers'
  | 'votes'
  | 'likes'
  | 'comments'
  | 'clients'
  | 'stats'
  | 'images'
  | 'volunteer'
  | 'empty'
  | 'magic'
  | 'magicSession'
  | 'empty';

export class ApiService {
  private readonly endpointPaths: { [key in AllowedEndpoints]: string } = {
    sessions: '/sessions',
    currentSession: '/sessions/current',
    users: '/users',
    currentUser: '/users/me',
    questionsList: '/approved-questions',
    questions: '/questions',
    answers: '/answers',
    votes: '/votes',
    likes: '/likes',
    comments: '/comments',
    clients: '/clients',
    stats: '/stats',
    images: '/files/images',
    volunteer: '/volunteer',
    magic: '/users/magic',
    magicSession: '/users/magic/confirm',
    empty: ''
  };
  private baseURL: string;
  private requestHeaders: { [key: string]: string } = {
    'Content-Type': 'application/json',
    'Accepts': 'application/json'
    // 'Authorization': localStorage.token || null
  };

  constructor() {
    this.baseURL = `${REACT_APP_API_URL}${API_BASE_PATH}`;
  }

  setUrl(endpoint: AllowedEndpoints, path: string = ''): string {
    return `${this.baseURL}${this.endpointPaths[endpoint]}${path}`;
  }

  updateRequestHeader(key: string, value: string): void {
    if (key.match(/Authorization/i) !== null) return;

    this.requestHeaders[key] = value;
  }

  removeRequestHeader(key: string): void {
    const { [key]: headerToRemove, ...headersToRemain } = this.requestHeaders;

    this.requestHeaders = headersToRemain;
  }

  setToken() {
    if (localStorage.token && this.requestHeaders['Authorization'] !== localStorage.token)
      this.requestHeaders['Authorization'] = localStorage.token;
  }

  get<T>(
    url: {
      endpoint: AllowedEndpoints;
      path?: string;
      fullURL?: string;
    },
    options?: { [key: string]: string }
  ): Promise<T> {
    const currentUrl = url?.fullURL ? url.fullURL : this.setUrl(url.endpoint, url?.path);
    const headers = this.requestHeaders

    // Current sessions issue
    if (url?.path?.match('/sessions/current') !== null && !this.requestHeaders['Authorization'])
      headers['Authorization'] = '';

    this.setToken()

    return axios.get(currentUrl, {
      headers: headers,
      ...options
    });
  }

  post<T>(
    url: {
      endpoint: AllowedEndpoints;
      path?: string;
      fullURL?: string;
    },
    data: { [key: string]: any },
    options?: { [key: string]: string }
  ): Promise<T> {
    const currentUrl = url?.fullURL ? url.fullURL : this.setUrl(url.endpoint, url?.path);

    this.setToken()

    return axios.post(currentUrl, data, {
      headers: this.requestHeaders,
      ...options
    });
  }

  put<T>(
    url: {
      endpoint: AllowedEndpoints;
      path?: string;
      fullURL?: string;
    },
    data: { [key: string]: any },
    options?: { [key: string]: string }
  ): Promise<T> {
    const currentUrl = url?.fullURL ? url.fullURL : this.setUrl(url.endpoint, url?.path);

    this.setToken()

    return axios.put(currentUrl, data, {
      headers: this.requestHeaders,
      ...options
    });
  }

  reportInvalidResponseError(errorIdentifier: string, response: any) {
    console.warn(`${errorIdentifier} -> Invalid Response`, response);
  }

  reportRequestError(errorIdentifier: string, error: any) {
    console.error(`${errorIdentifier} -> Invalid Response`, error);
  }
}
